import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  IconButton,
  Button,
  Typography,
  Paper,
  Switch,
  Tooltip,
  Skeleton,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SaveIcon from "@mui/icons-material/Save";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Swal from "sweetalert2";
import { api } from "../../utils/api";

/**
 * ServiceChecklistEditor
 * Props:
 *  - serviceId (number)   required
 *  - serviceName (string) for header
 *  - onClose   (fn)       close callback
 *  - onSaved   (fn)       called after successful save (for list refresh)
 */
export default function ServiceChecklistEditor({ serviceId, serviceName, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      const res = await api.get(`/services/checklist_list.php?service_id=${serviceId}`);
      const rows = (res.items || []).map((r) => ({
        id: r.id,
        item_text: r.item_text || "",
        is_required: r.is_required == 1,
      }));
      setItems(rows);
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Failed to load checklist" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [serviceId]);

  const addItem = () => {
    setItems((p) => [...p, { id: null, item_text: "", is_required: true }]);
  };

  const updateText = (idx, value) => {
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, item_text: value } : it)));
  };

  const toggleRequired = (idx) => {
    setItems((p) =>
      p.map((it, i) => (i === idx ? { ...it, is_required: !it.is_required } : it))
    );
  };

  const removeItem = (idx) => {
    setItems((p) => p.filter((_, i) => i !== idx));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    setItems((p) => {
      const next = [...p];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDown = (idx) => {
    setItems((p) => {
      if (idx === p.length - 1) return p;
      const next = [...p];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    const cleaned = items
      .map((it, i) => ({
        item_text: (it.item_text || "").trim(),
        sort_order: i,
        is_required: it.is_required ? 1 : 0,
      }))
      .filter((it) => it.item_text !== "");

    setSaving(true);
    try {
      await api.post("/services/checklist_save.php", {
        service_id: serviceId,
        items: cleaned,
      });
      Swal.fire({
        icon: "success",
        title: "Checklist Saved",
        timer: 1200,
        showConfirmButton: false,
      });
      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const requiredCount = items.filter((it) => it.is_required).length;

  return (
    <Box>
      {/* HEADER */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ChecklistIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Checklist Template
          </Typography>
          {serviceName && (
            <Typography variant="caption" color="text.secondary">
              For: {serviceName}
            </Typography>
          )}
        </Box>
        <Chip
          size="small"
          label={`${items.length} items`}
          color="primary"
          variant="outlined"
        />
        <Chip
          size="small"
          label={`${requiredCount} required`}
          color="warning"
          variant="outlined"
        />
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {/* LIST */}
      {loading ? (
        <Stack spacing={1}>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} height={52} />
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: "center",
            borderStyle: "dashed",
            bgcolor: "action.hover",
          }}
        >
          <Typography variant="h4" sx={{ mb: 1 }}>📋</Typography>
          <Typography variant="body2" color="text.secondary">
            No checklist items yet. Click "Add Item" to start.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {items.map((it, idx) => (
            <Paper
              key={idx}
              variant="outlined"
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  color: "text.disabled",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <DragIndicatorIcon fontSize="small" />
                <Typography
                  variant="caption"
                  sx={{ ml: 0.5, fontWeight: 700, minWidth: 20 }}
                >
                  {idx + 1}
                </Typography>
              </Box>

              <TextField
                size="small"
                placeholder="Checklist item text…"
                value={it.item_text}
                onChange={(e) => updateText(idx, e.target.value)}
                fullWidth
              />

              <Tooltip title={it.is_required ? "Required" : "Optional"}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Switch
                    size="small"
                    checked={it.is_required}
                    onChange={() => toggleRequired(idx)}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {it.is_required ? "Req" : "Opt"}
                  </Typography>
                </Box>
              </Tooltip>

              <Tooltip title="Move Up">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Move Down">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => moveDown(idx)}
                    disabled={idx === items.length - 1}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Stack>
      )}

      {/* ACTIONS */}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addItem}>
          Add Item
        </Button>
        <Box sx={{ flex: 1 }} />
        {onClose && (
          <Button color="inherit" onClick={onClose}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? "Saving..." : "Save Checklist"}
        </Button>
      </Stack>
    </Box>
  );
}
